import { defineStore } from 'pinia'
import type {
  TipoCadastro,
  ImportacaoStatus,
  ImportacaoState,
  ValidacaoLocalResult,
  ValidacaoApiResponse,
  ProcessamentoResult,
  ItemFalha
} from '@/types'

// Factory: gera um estado inicial NOVO a cada chamada.
// Precisa recriar os objetos/arrays aninhados (upload, itensFalha) — se reusássemos
// uma constante, o reset devolveria a mesma referência de `upload` já mutada por
// setArquivo, "ressuscitando" o arquivo anterior no campo de upload.
const criarEstadoInicial = (): ImportacaoState => ({
  tipoCadastro: null,
  status: 'idle',
  upload: {
    arquivo: null,
    nomeArquivo: '',
    tamanho: 0,
    progresso: 0
  },
  validacaoLocal: null,
  validacaoApi: null,
  processamento: null,
  erro: null,
  processados: 0,
  totalParaProcessar: 0,
  jobId: null,
  itensFalha: []
})

export const useImportacaoStore = defineStore('importacao', {
  state: (): ImportacaoState => criarEstadoInicial(),

  getters: {
    temArquivo: (state): boolean => state.upload.arquivo !== null,

    podeProcessar: (state): boolean => {
      return (
        state.status === 'validated' &&
        state.validacaoLocal !== null &&
        state.validacaoLocal.validos > 0
      )
    },

    totalErros: (state): number => {
      const errosLocal = state.validacaoLocal?.invalidos ?? 0
      const errosApi = state.validacaoApi?.data?.invalidos ?? 0
      return errosLocal + errosApi
    },

    statusLabel: (state): string => {
      const labels: Record<ImportacaoStatus, string> = {
        idle: 'Aguardando',
        uploading: 'Enviando arquivo...',
        validating: 'Validando dados...',
        validated: 'Pronto para importar',
        queued: 'Na fila...',
        processing: 'Importando registros...',
        importing: 'Importando registros...',
        completed: 'Importação concluída',
        error: 'Erro na importação'
      }
      return labels[state.status]
    }
  },

  actions: {
    setTipoCadastro(tipo: TipoCadastro) {
      this.tipoCadastro = tipo
      // Limpa dados anteriores ao mudar o tipo
      this.limparArquivo()
    },

    setStatus(status: ImportacaoStatus) {
      this.status = status
    },

    setArquivo(arquivo: File) {
      this.upload.arquivo = arquivo
      this.upload.nomeArquivo = arquivo.name
      this.upload.tamanho = arquivo.size
      this.upload.progresso = 0
      this.status = 'uploading'
    },

    setUploadProgresso(progresso: number) {
      this.upload.progresso = progresso
    },

    limparArquivo() {
      this.upload.arquivo = null
      this.upload.nomeArquivo = ''
      this.upload.tamanho = 0
      this.upload.progresso = 0
      this.validacaoLocal = null
      this.validacaoApi = null
      this.processamento = null
      this.erro = null
      this.status = 'idle'
      this.processados = 0
      this.totalParaProcessar = 0
      this.jobId = null
      this.itensFalha = []
    },

    setValidacaoLocal(resultado: ValidacaoLocalResult | null) {
      this.validacaoLocal = resultado
    },

    setValidacaoApi(resultado: ValidacaoApiResponse | null) {
      this.validacaoApi = resultado
    },

    setProcessamento(resultado: ProcessamentoResult) {
      this.processamento = resultado
      this.processados = resultado.inseridos + resultado.erros + resultado.duplicados
    },

    setProcessados(quantidade: number) {
      this.processados = quantidade
    },

    setTotalParaProcessar(total: number) {
      this.totalParaProcessar = total
    },

    setJobId(jobId: string | null) {
      this.jobId = jobId
    },

    setItensFalha(itens: ItemFalha[]) {
      this.itensFalha = itens
    },

    setErro(mensagem: string) {
      this.erro = mensagem
    },

    limparErro() {
      this.erro = null
    },

    resetar() {
      Object.assign(this, criarEstadoInicial())
    }
  }
})
